import React, { useState } from 'react';
import PageApp from '../../../Page';
import { HomeOutlined, FilePdfOutlined, UpOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, message, Card, Form, Table, Space, Checkbox, Spin, Empty, Divider } from 'antd';
import AnioPeriodo from '../../../../components/forms/anio_periodo';
import resumenNominaService from '../../../../apis/ResumenNominaService';

const routes = [
  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: (
      <>
        <FilePdfOutlined />
        <span>Resumen de Nómina PDF</span>
      </>
    ),
  }
];

const ResumenNominaPDF = () => {
  const [periodForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [disabledForm, setDisabled] = useState(true);
  const [header, setHeader] = useState(null);
  const [generaciónPersonalizada, setGeneraciónPersonalizada] = useState(false);
  
  // Estados para preview
  const [previewData, setPreviewData] = useState(null);
  const [percepciones, setPercepciones] = useState([]);
  const [deducciones, setDeducciones] = useState([]);
  const [otros, setOtros] = useState([]);
  
  // Callback para cuando se selecciona un período
  const onBlurHeader = async (values, periodData) => {
    setHeader(values);
    if (values?.anio && values?.num_periodo && values?.periodo_id) {
      setDisabled(false);
      // Cargar preview automáticamente
      await cargarPreview(values.num_periodo, values.anio, values.periodo_id);
    } else {
      setDisabled(true);
      setPreviewData(null);
      setPercepciones([]);
      setDeducciones([]);
      setOtros([]);
    }
  };

  // Cargar preview de conceptos
  const cargarPreview = async (num_periodo, anio, tipo_periodo) => {
    try {
      setPreviewLoading(true);
      const preview = await resumenNominaService.obtenerPreview(num_periodo, anio, tipo_periodo);
      setPreviewData(preview);
      
      // Ordenar con índices para mantener orden inicial
      const percepcionesOrdenadas = preview.percepciones.map((p, idx) => ({
        ...p,
        orden: p.orden !== undefined ? p.orden : idx
      })).sort((a, b) => a.orden - b.orden);
      
      const deduccionesOrdenadas = preview.deducciones.map((d, idx) => ({
        ...d,
        orden: d.orden !== undefined ? d.orden : idx
      })).sort((a, b) => a.orden - b.orden);
      
      const otrosOrdenados = preview.otros.map((o, idx) => ({
        ...o,
        orden: o.orden !== undefined ? o.orden : idx
      })).sort((a, b) => a.orden - b.orden);
      
      setPercepciones(percepcionesOrdenadas);
      setDeducciones(deduccionesOrdenadas);
      setOtros(otrosOrdenados);
      setGeneraciónPersonalizada(true);
      
      message.success('Preview cargado exitosamente');
    } catch (error) {
      console.error('Error cargando preview:', error);
      message.error('Error al cargar preview: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Generar PDF con configuración default
  const generarPDFDefault = async () => {
    try {
      setLoading(true);
      const periodData = periodForm.getFieldsValue();
      await resumenNominaService.generarYDescargarPDF(periodData.num_periodo, periodData.anio, periodData.periodo_id);
      message.success('PDF generado y descargado exitosamente');
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al generar el PDF: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Generar PDF con configuración personalizada
  const generarPDFPersonalizado = async () => {
    try {
      setLoading(true);
      const periodData = periodForm.getFieldsValue();
      
      const config = {
        num_periodo: periodData.num_periodo,
        anio: periodData.anio,
        tipo_periodo: periodData.periodo_id,
        percepciones: percepciones.filter(p => p.seleccionado).map((p, idx) => ({
          codigo: p.codigo,
          concepto: p.concepto,
          seleccionado: p.seleccionado,
          orden: idx
        })),
        deducciones: deducciones.filter(d => d.seleccionado).map((d, idx) => ({
          codigo: d.codigo,
          concepto: d.concepto,
          seleccionado: d.seleccionado,
          orden: idx
        })),
        otros: otros.filter(o => o.seleccionado).map((o, idx) => ({
          codigo: o.codigo,
          concepto: o.concepto,
          seleccionado: o.seleccionado,
          orden: idx
        }))
      };
      
      await resumenNominaService.generarPDFPersonalizado(config);
      message.success('PDF personalizado generado y descargado');
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al generar PDF: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Funciones de manejo de conceptos
  const toggleConcepto = (tipo, index) => {
    if (tipo === 'percepciones') {
      const updated = [...percepciones];
      updated[index].seleccionado = !updated[index].seleccionado;
      setPercepciones(updated);
    } else if (tipo === 'deducciones') {
      const updated = [...deducciones];
      updated[index].seleccionado = !updated[index].seleccionado;
      setDeducciones(updated);
    } else {
      const updated = [...otros];
      updated[index].seleccionado = !updated[index].seleccionado;
      setOtros(updated);
    }
  };

  const moverConcepto = (tipo, index, direccion) => {
    let array;
    if (tipo === 'percepciones') {
      array = [...percepciones];
    } else if (tipo === 'deducciones') {
      array = [...deducciones];
    } else {
      array = [...otros];
    }

    if (direccion === 'arriba' && index > 0) {
      [array[index], array[index - 1]] = [array[index - 1], array[index]];
    } else if (direccion === 'abajo' && index < array.length - 1) {
      [array[index], array[index + 1]] = [array[index + 1], array[index]];
    }

    if (tipo === 'percepciones') {
      setPercepciones(array);
    } else if (tipo === 'deducciones') {
      setDeducciones(array);
    } else {
      setOtros(array);
    }
  };

  const eliminarConcepto = (tipo, index) => {
    if (tipo === 'percepciones') {
      setPercepciones(percepciones.filter((_, i) => i !== index));
    } else if (tipo === 'deducciones') {
      setDeducciones(deducciones.filter((_, i) => i !== index));
    } else {
      setOtros(otros.filter((_, i) => i !== index));
    }
  };

  // Columnas para las tablas
  const crearColumnasTabla = (tipo) => [
    {
      title: 'Sel',
      width: 50,
      render: (_, record, index) => (
        <Checkbox
          checked={record.seleccionado}
          onChange={() => toggleConcepto(tipo, index)}
        />
      ),
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      width: 80,
    },
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      width: 200,
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      width: 100,
      render: (monto) => `$${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Exento',
      dataIndex: 'exento',
      width: 100,
      render: (exento) => `$${(exento || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Acciones',
      width: 120,
      render: (_, record, index) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<UpOutlined />}
            disabled={index === 0}
            onClick={() => moverConcepto(tipo, index, 'arriba')}
          />
          <Button
            type="text"
            size="small"
            icon={<DownOutlined />}
            disabled={index === (tipo === 'percepciones' ? percepciones.length - 1 : tipo === 'deducciones' ? deducciones.length - 1 : otros.length - 1)}
            onClick={() => moverConcepto(tipo, index, 'abajo')}
          />
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => eliminarConcepto(tipo, index)}
          />
        </Space>
      ),
    },
  ];

  return (
    <PageApp
      routes={routes}
      title={'Resumen de Nómina PDF'}>
      
      {/* Componente AnioPeriodo */}
      <AnioPeriodo 
        onBlur={onBlurHeader} 
        form={periodForm} 
        disabledForm={false}
      />
      
      <br/>
      
      {/* Botones de acción */}
      {header && !disabledForm && (
        <Card style={{ marginBottom: '24px' }}>
          <Space>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={generarPDFDefault}
              loading={loading}
              size="large"
            >
              Generar PDF (Por Defecto)
            </Button>
            <Button
              type="dashed"
              icon={<FilePdfOutlined />}
              onClick={generarPDFPersonalizado}
              loading={loading}
              disabled={!generaciónPersonalizada || previewLoading}
              size="large"
            >
              Generar PDF (Personalizado)
            </Button>
          </Space>
        </Card>
      )}

      {/* Contenedor de configuración */}
      {generaciónPersonalizada && previewData && (
        <Spin spinning={previewLoading}>
          <Card 
            title="Configuración de Conceptos"
            style={{ marginBottom: '24px' }}
          >
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Selecciona los conceptos a incluir, reordénalos arrastrando hacia arriba/abajo, y elimina los que no necesites.
            </p>

            {/* Percepciones */}
            {percepciones && percepciones.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ color: '#1890ff', marginBottom: '16px' }}>Percepciones</h3>
                <Table
                  columns={crearColumnasTabla('percepciones')}
                  dataSource={percepciones}
                  pagination={false}
                  size="small"
                  rowKey={(record, index) => `percepcion-${index}`}
                />
              </div>
            )}

            {/* Divider */}
            {percepciones.length > 0 && deducciones.length > 0 && <Divider />}

            {/* Deducciones */}
            {deducciones && deducciones.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ color: '#ff4d4f', marginBottom: '16px' }}>Deducciones</h3>
                <Table
                  columns={crearColumnasTabla('deducciones')}
                  dataSource={deducciones}
                  pagination={false}
                  size="small"
                  rowKey={(record, index) => `deduccion-${index}`}
                />
              </div>
            )}

            {/* Divider */}
            {(percepciones.length > 0 || deducciones.length > 0) && otros.length > 0 && <Divider />}

            {/* Otros */}
            {otros && otros.length > 0 && (
              <div>
                <h3 style={{ color: '#faad14', marginBottom: '16px' }}>Otros Conceptos</h3>
                <Table
                  columns={crearColumnasTabla('otros')}
                  dataSource={otros}
                  pagination={false}
                  size="small"
                  rowKey={(record, index) => `otro-${index}`}
                />
              </div>
            )}

            {percepciones.length === 0 && deducciones.length === 0 && otros.length === 0 && (
              <Empty description="Sin conceptos disponibles" />
            )}

            {/* Resumen de selección */}
            <Divider />
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f0f2f5', 
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <p>
                <strong>Percepciones:</strong> {percepciones.filter(p => p.seleccionado).length} de {percepciones.length} seleccionadas
              </p>
              <p>
                <strong>Deducciones:</strong> {deducciones.filter(d => d.seleccionado).length} de {deducciones.length} seleccionadas
              </p>
              <p>
                <strong>Otros:</strong> {otros.filter(o => o.seleccionado).length} de {otros.length} seleccionados
              </p>
            </div>
          </Card>
        </Spin>
      )}
    </PageApp>
  );
};

export default ResumenNominaPDF;


